import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { MarketplaceMobileLayout } from './MarketplaceMobileLayout';

interface MarketplaceScreenLayoutProps {
  children: React.ReactNode;
  /** Optional title for the mobile header. Defaults to 'Community mar...' */
  title?: string;
  /** Title shown in the navigator header for desktop web fallback */
  navigatorTitle?: string;
  /** Whether to show the notification bell in the mobile header */
  showNotification?: boolean;
}

/**
 * A wrapper component that handles responsive layout switching for marketplace screens.
 * 
 * - Mobile/Tablet: Uses MarketplaceMobileLayout with hamburger menu and gradient frame
 * - Desktop: Uses a simple container (DesktopNavigator provides the header)
 * 
 * This component also handles hiding/showing the navigator header appropriately.
 */
export const MarketplaceScreenLayout: React.FC<MarketplaceScreenLayoutProps> = ({
  children,
  title,
  navigatorTitle = 'Marketplace',
  showNotification = true,
}) => {
  const navigation = useNavigation<any>();
  const { layoutMode, isWeb } = useResponsive();
  
  const isDesktop = layoutMode === 'desktop';
  const useMobileLayout = !isDesktop;

  // Handle navigator header visibility
  // - Mobile/Tablet: Hide (uses MarketplaceMobileLayout's built-in header)
  // - Desktop Web: Hide (DesktopNavigator has its own header)
  // - Fallback (native desktop/other): Show navigator header
  useEffect(() => {
    if (useMobileLayout || (isWeb && isDesktop)) {
      navigation.setOptions({ headerShown: false });
    } else {
      navigation.setOptions({ 
        headerShown: true,
        title: navigatorTitle,
      });
    }
  }, [navigation, useMobileLayout, isDesktop, isWeb, navigatorTitle]);

  // Mobile and Tablet: Use the full MarketplaceMobileLayout with header and gradient frame
  if (useMobileLayout) {
    return (
      <MarketplaceMobileLayout 
        title={title}
        showNotification={showNotification}
      >
        {children}
      </MarketplaceMobileLayout>
    );
  }

  // Desktop: Simple container (DesktopNavigator provides header)
  return (
    <View style={styles.container}>
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.background,
  },
});
